<template>
  <div
    class="mb-8 grid gap-4 mobile:grid-cols-1"
    :class="{ 'desktop:grid-flow-col': textArea }"
  >
    <EditableArea
      v-if="textArea"
      class="desktop:col-span-1"
      :content="textArea"
    />
    <div class="rounded border border-neutral-100 p-6 mobile:px-4 desktop:col-span-2">
      <EditableArea
        v-if="titleArea"
        :content="titleArea"
      />
      <form
        autocomplete="off"
        @submit.prevent="onSendEmail"
      >
        <KikDynamicForm
          ref="dynamicForm"
          :values="form"
          :fields="fields"
          @on-form-update="onFormUpdate"
        >
          <div>
            <EditableArea
              v-if="termsConditionsArea"
              class="mb-4 font-light"
              :content="termsConditionsArea"
            />
            <KikButton
              class="min-w-40 mobile:min-w-full"
              :disabled="errors || loading"
              v-bind="{ type: 'submit' }"
            >
              <SfLoaderCircular v-if="loading" />
              <span v-else>{{ btnLabel }}</span>
            </KikButton>
          </div>
        </KikDynamicForm>
      </form>
    </div>
  </div>
</template>

<script lang="ts" setup>
// @ts-expect-error missing declaration file with types
import { EditableArea } from '@magnolia/vue-editor/dist/vue-editor.es.js';
import { SfLoaderCircular } from '@storefront-ui/vue';
import { useForm } from '~/composables/custom/useForm/useForm';
import { useTracking } from '~/composables/custom/useTracking';

defineComponent({ name: 'KikForm' });

interface Props {
  termsConditionsArea: any;
  titleArea: any;
  textArea: any;
  btnLabel: string;
  sideBySide: boolean;
  subjectOptions: object[];
  salutationOptions: string[];
}

const props = withDefaults(defineProps<Props>(), {
  termsConditionsArea: null,
  titleArea: null,
  textArea: null,
  btnLabel: 'Send',
  sideBySide: false,
  subjectOptions: () => [],
  salutationOptions: () => [],
});

const route = useRoute();

const { loading, sendEmail, errors } = useForm();
const { trackError } = useTracking();
const dynamicForm = ref();

const form: any = ref({});

const onSendEmail = async () => {
  if (dynamicForm.value.isInvalid()) {
    const errors = dynamicForm.value.errors();
    Object.entries<string>(errors).forEach(([key, value]) => {
      trackError(`Form input - my account address - ${key}`, value);
    });
    return;
  }

  await sendEmail({
    ...form.value,
    path: route.path,
  });

  if (!errors.value) {
    dynamicForm.value.clearForm();
  }
};

const onFormUpdate = (value: unknown) => {
  form.value = value;
};

const fields = computed(() => ({
  'salutation': {
    type: 'option',
    value: props.salutationOptions?.map(key => ({ key, label: key })) || [],
    rules: 'required',
    label: 'Salutation',
  },
  'name': {
    type: 'text',
    label: 'Name',
    rules: 'required',
  },
  'email': {
    type: 'text',
    label: 'E-mail address',
    rules: 'required|minmax:2,50|email',
    onChange: 'trim',
  },
  'subject': {
    label: 'Subject',
    type: 'option',
    value: props.subjectOptions.map((option) => {
      return {
        ...option,
        key: option.id,
      };
    }) || [],
  },
  'orderNumber': {
    type: 'text',
    label: 'Order number',
    onChange: 'number',
  },
  'add.subject': {
    type: 'text',
    label: 'Other topics',
  },
  'message': {
    type: 'textArea',
    rules: 'required',
    label: 'Message',
  },
}));
</script>
